import React, { useEffect, useState } from "react";
import VisorImagen from "../../Components/VisorImagen";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Space, Spin, Tag, Typography, Result } from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileUnknownOutlined,
  LockOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { apiFetch, getAuthToken } from "../../services/apiClient";
import { useAuth } from "../../context/AuthContext";
import { isAdminRole } from "../../utils/roles.utils";
import { resolveFileUrl } from "../../utils/safeUrl";

const { Text } = Typography;

interface DocumentMeta {
  originalName: string;
  mimeType: string;
  url: string;
}

type ValidSource = "tareas" | "contratos";

const SOURCE_CONFIG: Record<
  ValidSource,
  { endpoint: (id: string) => string; arrayKey?: string }
> = {
  tareas: { endpoint: (id) => `/tareas/${id}/documents` },
  contratos: { endpoint: (id) => `/contratos/${id}/archivos` },
};

// `resolveFileUrl` se importa desde `utils/safeUrl.ts` — centralizado para
// reutilización en aseos.tsx y otras páginas que rendericen URLs de la API.
// Ver round 2026-04-26 (P1-I).

function isOfficeOrText(mimeType: string): boolean {
  const officeTypes = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/msword",
    "application/vnd.ms-excel",
    "application/vnd.ms-powerpoint",
    "text/plain",
    "text/csv",
  ];
  return officeTypes.includes(mimeType);
}

const DocumentViewerPage: React.FC = () => {
  const { source, id, fileIndex } = useParams<{
    source: string;
    id: string;
    fileIndex: string;
  }>();
  const navigate = useNavigate();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { user } = useAuth();

  // Para el cliente el visor es estrictamente solo-lectura: ver, descargar,
  // leer e imprimir. Nunca editar, dibujar, anotar. Se fuerza gview (Google
  // Docs viewer) incluso para PDFs — el visor nativo de Firefox expone
  // herramientas de dibujo/anotación que no queremos mostrar al cliente.
  // Admin/superadmin mantienen el visor nativo por comodidad (pueden usar
  // las tools del browser si quieren).
  const isReadOnlyViewer = !isAdminRole(user?.rol);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<DocumentMeta | null>(null);

  useEffect(() => {
    if (!source || !id || fileIndex === undefined) {
      setError("Parámetros de ruta inválidos");
      setLoading(false);
      return;
    }

    const config = SOURCE_CONFIG[source as ValidSource];
    if (!config) {
      setError(`Fuente "${source}" no soportada`);
      setLoading(false);
      return;
    }

    const idx = parseInt(fileIndex, 10);
    if (isNaN(idx) || idx < 0) {
      setError("Índice de archivo inválido");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDocument() {
      setLoading(true);
      setError(null);

      const res = await apiFetch<{ success: boolean; data: any[] }>(
        config.endpoint(id!),
        { errorMsg: "Error al obtener los documentos" },
      );

      if (cancelled) return;

      if (!res || !res.data) {
        setError("No se pudieron obtener los documentos");
        setLoading(false);
        return;
      }

      const files = Array.isArray(res.data) ? res.data : [];

      if (idx >= files.length) {
        setError("Archivo no encontrado");
        setLoading(false);
        return;
      }

      const file = files[idx];
      setDoc({
        originalName: file.originalName ?? file.nombre ?? `archivo-${idx}`,
        mimeType: file.mimeType ?? file.tipo ?? "application/octet-stream",
        url: file.url ?? file.ruta ?? "",
      });
      setLoading(false);
    }

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [source, id, fileIndex]);

  const fullUrl = doc ? resolveFileUrl(doc.url) : "";
  const [viewerLoading, setViewerLoading] = useState(true);

  useEffect(() => {
    if (doc) setViewerLoading(true);
  }, [doc]);

  // PDF: lo cargamos como blob AUTENTICADO (con la sesión del usuario) en vez
  // de delegar en Google gview. Razón (round 2026-06-15, post-migración R2 →
  // storage local): los archivos locales se sirven desde /files/<token> con un
  // JWT ligado al usuario (`aud`); los servidores de Google (gview) no pueden
  // autenticarse, así que gview devolvía "blocked". Con un fetch propio (cookie
  // de sesión + Bearer) sí se autoriza, y el resultado se muestra desde un
  // `blob:` same-origin — sin depender de terceros ni de framing cross-origin.
  // Aplica a admin y cliente; el cliente además oculta el toolbar nativo.
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    if (!doc || doc.mimeType !== "application/pdf") {
      setPdfBlobUrl(null);
      return;
    }
    let cancelled = false;
    let objUrl: string | null = null;
    setPdfError(false);
    setViewerLoading(true);
    (async () => {
      try {
        const token = getAuthToken();
        const resp = await fetch(resolveFileUrl(doc.url), {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objUrl);
      } catch {
        if (!cancelled) {
          setPdfError(true);
          setViewerLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [doc]);

  const iframeStyle: React.CSSProperties = {
    width: "100%",
    height: "calc(100vh - 120px)",
    border: "none",
    display: viewerLoading ? "none" : "block",
  };

  const loadingOverlay = viewerLoading && (
    <div style={{ textAlign: "center", padding: 64 }}>
      <Spin size="large" tip="Generando previsualización..." />
    </div>
  );

  const renderViewer = () => {
    if (!doc) return null;

    const { mimeType } = doc;

    if (mimeType === "application/pdf") {
      // El PDF se carga como blob autenticado (ver el useEffect de arriba).
      if (pdfError) {
        return (
          <Result
            status="warning"
            title="No se pudo cargar la previsualización"
            subTitle="Puedes descargar el documento con el botón de arriba."
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Descargar
              </Button>
            }
          />
        );
      }
      if (!pdfBlobUrl) {
        // Mientras se descarga el blob.
        return (
          <div style={{ textAlign: "center", padding: 64 }}>
            <Spin size="large" tip="Generando previsualización..." />
          </div>
        );
      }
      // Cliente (solo-lectura): ocultamos el toolbar nativo del visor de PDF
      // (#toolbar=0&navpanes=0) para no ofrecer descarga/impresión/anotación
      // desde el propio visor — el botón "Descargar" de arriba cubre el caso
      // legítimo. Admin: visor completo con todas las herramientas del browser.
      const frag = isReadOnlyViewer ? "#toolbar=0&navpanes=0&scrollbar=0" : "";
      return (
        <>
          {loadingOverlay}
          <iframe
            src={`${pdfBlobUrl}${frag}`}
            title={doc.originalName}
            style={iframeStyle}
            onLoad={() => setViewerLoading(false)}
          />
        </>
      );
    }

    if (mimeType.startsWith("image/")) {
      return (
        <>
          {loadingOverlay}
          <div style={{ display: viewerLoading ? "none" : "block" }}>
            <VisorImagen src={fullUrl} alt={doc.originalName} altura="calc(100vh - 230px)"
              onLoad={() => setViewerLoading(false)}
              onError={() => setViewerLoading(false)}
              onContextMenu={isReadOnlyViewer ? (e) => e.preventDefault() : undefined} />
          </div>
        </>
      );
    }

    if (isOfficeOrText(mimeType)) {
      const gviewUrl = `https://docs.google.com/gview?url=${encodeURIComponent(fullUrl)}&embedded=true`;
      return (
        <>
          {loadingOverlay}
          <iframe
            src={gviewUrl}
            title={doc.originalName}
            style={iframeStyle}
            sandbox={
              isReadOnlyViewer
                ? "allow-scripts allow-same-origin allow-popups"
                : undefined
            }
            onLoad={() => setViewerLoading(false)}
          />
        </>
      );
    }

    return (
      <Result
        icon={<FileUnknownOutlined />}
        title="No se puede previsualizar este archivo"
        subTitle={`Tipo: ${mimeType}`}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Descargar
          </Button>
        }
      />
    );
  };

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="historial-page"
    >
      <main style={{ padding: 24 }}>
        <PageHeader
          title={doc?.originalName ?? "Visor de documentos"}
          helpText="Visualiza y descarga documentos adjuntos."
        />

        {!loading && !error && doc && (
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            >
              Volver
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar
            </Button>
            {/* Imprimir — abre el diálogo del navegador sobre el PDF/imagen
                actualmente visible en el iframe. Tanto admin como cliente
                pueden imprimir; el cliente solo tiene este camino para
                obtener una copia en papel. */}
            <Button
              icon={<PrinterOutlined />}
              onClick={() => {
                // Abrir el archivo en una nueva ventana y lanzar print().
                // Más confiable que window.print() sobre el iframe embebido
                // (que a menudo no expone print desde cross-origin).
                const w = window.open(fullUrl, "_blank", "noopener,noreferrer");
                if (w) {
                  // Pequeño retraso para que cargue antes de imprimir.
                  setTimeout(() => w.print(), 800);
                }
              }}
            >
              Imprimir
            </Button>
            <Text type="secondary">{doc.mimeType}</Text>
            {isReadOnlyViewer ? (
              <Tag
                icon={<LockOutlined />}
                color="blue"
                style={{ marginLeft: 4 }}
              >
                Solo lectura
              </Tag>
            ) : null}
          </Space>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: 64 }}>
            <Spin size="large" tip="Cargando..." />
          </div>
        )}

        {error && (
          <Result
            status="error"
            title="Archivo no encontrado"
            subTitle={error}
            extra={
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
              >
                Volver
              </Button>
            }
          />
        )}

        {!loading && !error && renderViewer()}
      </main>
    </Layout>
  );
};

export default DocumentViewerPage;
